import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { PERMISSIONS, ROLES, isSuperAdmin, type TabName } from '@/config/permissions';

interface UserPermission {
  module: string;
  permission_level: string;
}

export const usePermissions = () => {
  const { user, userProfile, userRole: authUserRole } = useAuth(); // Get userRole from AuthProvider
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && userProfile) {
      setPermissions([]);
      setLoading(false);
    } else if (user && !userProfile) {
      // Still loading user profile
      setLoading(true);
    } else {
      setPermissions([]);
      setLoading(false);
    }
  }, [user, userProfile]);

  const hasPermission = (module: string, requiredLevel: 'view' | 'edit' | 'full'): boolean => {
    if (!user || loading) return false;

    // Super admin (stamatis/lefki) has access to everything
    if (isSuperAdmin({ ...userProfile, email: user.email })) {
      return true;
    }

    const currentRole = authUserRole || 'user'; // Use role from AuthProvider
    
    // Map tab names to permission keys
    const tabPermissions: Record<string, keyof typeof PERMISSIONS> = {
      'dashboard': 'VIEW_DASHBOARD',
      'clients': 'VIEW_CLIENTS', 
      'people': 'VIEW_CLIENTS', // People are part of client management
      'proposals': 'VIEW_PROPOSALS',
      'projects': 'VIEW_PROPOSALS', // Projects are part of proposal management
      'activities': 'VIEW_DASHBOARD', // Activities are part of dashboard
      'xero-lite': 'VIEW_INVOICES',
      'invoices': 'VIEW_INVOICES',
      'users': 'VIEW_USERS',
      'initiatives': 'VIEW_DASHBOARD', // Initiatives are part of general access
      'wisdrop': 'VIEW_DASHBOARD', // Wisdrop is part of general access
      'settings': 'VIEW_SETTINGS'
    };

    const permissionKey = tabPermissions[module];
    if (!permissionKey) return false;

    const allowedRoles = PERMISSIONS[permissionKey];
    return allowedRoles.includes(currentRole as any);
  };

  const canView = (module: string): boolean => hasPermission(module, 'view');
  const canEdit = (module: string): boolean => hasPermission(module, 'edit');
  const canCreate = (module: string): boolean => hasPermission(module, 'full');
  const canDelete = (module: string): boolean => hasPermission(module, 'full');

  const getPermissionLevel = (module: string): string => {
    if (!user || loading) return 'none';
    
    // Super admin gets full access
    if (isSuperAdmin({ ...userProfile, email: user.email })) {
      return 'full';
    }

    const currentRole = authUserRole || 'user';
    
    // Check edit/create permissions based on role
    const editPermissions: Record<string, keyof typeof PERMISSIONS> = {
      'clients': 'EDIT_CLIENTS',
      'proposals': 'EDIT_PROPOSALS', 
      'invoices': 'EDIT_INVOICES',
      'users': 'EDIT_USERS',
      'settings': 'EDIT_SETTINGS'
    };

    const editPermissionKey = editPermissions[module];
    if (editPermissionKey && PERMISSIONS[editPermissionKey].includes(currentRole as any)) {
      return 'full';
    }

    // If user can view but not edit, return view
    if (hasPermission(module, 'view')) {
      return 'view';
    }

    return 'none';
  };

  return {
    permissions,
    loading,
    userRole: authUserRole, // Return the role from AuthProvider
    hasPermission,
    canView,
    canEdit,
    canCreate,
    canDelete,
    getPermissionLevel,
    refetch: () => {}, // No-op since we don't need to fetch anything
  };
};